/**
 * CW-SF-M5 TKT-6 — "My Recent SF Activities" widget.
 *
 * Tasks + Events I own, modified last 7 days, sorted by
 * LastModifiedDate DESC. Task vs Event chip distinguishes them;
 * completed tasks styled muted.
 */
import { useCallback } from 'react';
import { ExternalLink } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useAutoRefresh } from '@/services/widgetRegistry';
import type { WidgetProps } from '@/types';
import { SfWidgetShell } from './SfWidgetShell';
import { formatRelativeMinutes, type SfWidgetEnvelope } from './sfWidgetUtils';

interface ActivityItem {
  external_file_id: string;
  name: string;
  entity_type: 'task' | 'event';
  is_closed: boolean | null;
  activity_date: string | null;
  start_datetime: string | null;
  end_datetime: string | null;
  web_url: string | null;
  last_modified_at: string | null;
}
interface ApiResponse extends SfWidgetEnvelope {
  items: ActivityItem[];
}

const REFRESH_S = 5 * 60;

export function SfMyRecentActivitiesWidget(_props: WidgetProps) {
  const fetcher = useCallback(
    () => apiFetch<ApiResponse>('/mod/cw/widgets/sf/my-recent-activities?limit=20'),
    [],
  );
  const { data, loading, error } = useAutoRefresh<ApiResponse>(fetcher, REFRESH_S);

  return (
    <div className="sf-widget sf-widget--activities">
      <SfWidgetShell
        envelope={data}
        loading={loading}
        error={error}
        hasItems={(data?.items?.length ?? 0) > 0}
        emptyMessage="No tasks or events in the last 7 days."
      >
        <ul className="sf-widget__list">
          {data?.items.map((a) => {
            const muted = a.entity_type === 'task' && a.is_closed === true;
            return (
              <li
                key={a.external_file_id}
                className={
                  muted ? 'sf-widget__row sf-widget__row--muted' : 'sf-widget__row'
                }
              >
                <a
                  href={a.web_url ?? '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sf-widget__row-name"
                >
                  {a.name}
                  <ExternalLink size={10} />
                </a>
                <div className="sf-widget__row-chips">
                  <span
                    className={
                      a.entity_type === 'task'
                        ? 'sf-widget__chip sf-widget__chip--task'
                        : 'sf-widget__chip sf-widget__chip--event'
                    }
                  >
                    {a.entity_type === 'task' ? 'Task' : 'Event'}
                  </span>
                  {a.last_modified_at && (
                    <span className="sf-widget__chip">
                      {formatRelativeMinutes(a.last_modified_at)}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </SfWidgetShell>
    </div>
  );
}
